/*
 * Shared rules for the vehicle make/model catalogue that the make/model selectors read.
 *
 * ⚠ Why this exists. Three components carried byte-identical copies of the same two computeds -
 * MakeModelSelector, RoMakeModelSelector and configuration/vehicleMakes/VehicleMakeModel - and all
 * three had the same pair of defects:
 *
 *   - the model list never excluded DELETED catalogue entries, so an operator could pick a model that
 *     had been retired and was only told after the fact, by onSave refusing to save. That is Garrett's
 *     "it's constantly showing deleted cars... why is it there to choose from?" (31 Aug).
 *   - the year list was never sorted. It was built with uniqBy over a `versions` copy that the
 *     selectedMake watcher produced with lodash `reverse`, which mutates in place - so it reversed the
 *     shared Vuex catalogue for every other screen and the order flip-flopped on each re-selection.
 *     That is "It should be highest to lowest, not lowest to highest", and why it was never
 *     reproducible on demand.
 *
 * ⚠ There is no lower bound on the year, deliberately. The catalogue legitimately holds pre-war stock
 * (a 1933 Twin Coach, a 1947 Lancia Aprilia), so rejecting old years would reject real vehicles. The
 * upper bound is the operator's own rule: "that should be 2028, really, and then 27 and 26".
 */

// Two years ahead - next year's and the year after's models are orderable, nothing beyond that.
export const maxProductionYear = () => new Date().getFullYear() + 2

export const isPlausibleProductionYear = (year) => {
  const parsed = Number(year)
  return Number.isFinite(parsed) && parsed > 0 && parsed <= maxProductionYear()
}

/*
 * ⚠ Both markers are checked. The domain sets Status = Deleted AND appends _DELETED to the name
 * (VehicleMakeVersion.MarkDeleted), and the payload carries both, so either alone identifies a retired
 * model. Note `indexOf(...) !== -1`, not the `> 0` the components used - that older form would miss a
 * name that begins with the marker.
 */
const isDeletedVersion = (version) =>
  !!version &&
  (version.status === 'Deleted' ||
    (typeof version.name === 'string' && version.name.indexOf('_DELETED') !== -1))

const isKeptVersion = (version, keepModelId) =>
  !!version && keepModelId !== null && keepModelId !== undefined && version.id === keepModelId

/*
 * The catalogue entries an operator may choose from.
 *
 * ⚠ `keepModelId` re-admits the model the record is ALREADY on. Without it a vehicle whose model was
 * retired after it was booked would lose its selection the moment the dropdown was reopened, which
 * reads as data loss. It is re-admitted, not un-flagged: the caller still shows the "no longer active"
 * banner, so a dead model can be displayed but not newly chosen.
 */
const selectableVersions = (versions, keepModelId) => {
  const catalogue = Array.isArray(versions) ? versions : []
  return catalogue.filter((x) => !isDeletedVersion(x) || isKeptVersion(x, keepModelId))
}

/*
 * Distinct production years, newest first - with anything beyond the plausible range sunk to the bottom.
 *
 * ⚠ Derived from the SAME selectable set as the model list, so the two cannot disagree - a year whose
 * only models are deleted would otherwise be offered and then show an empty model dropdown.
 * ⚠ Numeric coercion is load-bearing: the list can hold strings as well as numbers, because a year
 * typed into the taggable field arrives from the multiselect as a string.
 *
 * ⚠ The list is PARTITIONED, not merely sorted, and it reuses isPlausibleProductionYear rather than
 * restating the rule. A strict descending sort puts the catalogue's impossible years at the very TOP -
 * 2034 and 2037 are live rows this branch stopped creating but deliberately did not delete - which is
 * the opposite of "it should be highest to lowest... maybe two years ahead of where we are, because
 * that's the most likely place to pick".
 *
 * ⚠ They are moved, NOT removed. An existing record may already sit on one, and a year the operator
 * types is theirs to choose - so the out-of-range group stays selectable, just out of the way.
 * ⚠ And "out of range" is the UPPER bound only, so 999 and a genuine 1933 stay in the main group.
 */
export const sortedProductionYears = (versions, keepModelId) => {
  const years = []
  selectableVersions(versions, keepModelId).forEach((version) => {
    const parsed = Number(version.productionYear)
    if (!Number.isFinite(parsed)) return
    if (years.indexOf(parsed) === -1) years.push(parsed)
  })
  const byNewestFirst = (a, b) => b - a
  const plausible = years.filter(isPlausibleProductionYear).sort(byNewestFirst)
  const outOfRange = years.filter((year) => !isPlausibleProductionYear(year)).sort(byNewestFirst)
  return plausible.concat(outOfRange)
}

/*
 * The models offered for the selected year.
 *
 * ⚠ The future-year fallback is PRESERVED behaviour, not an accident. When the selected year is beyond
 * the current one the current year's models are merged in, because a next-model-year car routinely
 * arrives before the catalogue carries its model. Removing it would break cataloguing new arrivals -
 * and it is also the client half of the server's clone-on-mismatch path in
 * InventoryService.GetInventoryModelMakeVersion.
 */
export const selectableModels = (versions, selectedYear, keepModelId) => {
  const selectable = selectableVersions(versions, keepModelId)
  if (selectable.length === 0) return []
  if (!selectedYear) return selectable

  const year = Number(selectedYear)
  const result = selectable.filter((x) => Number(x.productionYear) === year)

  const currentYear = new Date().getFullYear()
  if (year > currentYear) {
    selectable
      .filter((x) => Number(x.productionYear) === currentYear)
      .forEach((element) => {
        const index = result.findIndex((x) => x.name === element.name && x.series === element.series)
        if (index === -1) result.push(element)
      })
  }

  //The record's own model stays offered even if its year no longer lines up with the selection.
  if (keepModelId !== null && keepModelId !== undefined && !result.some((x) => x.id === keepModelId)) {
    const kept = selectable.find((x) => x.id === keepModelId)
    if (kept) result.push(kept)
  }

  return result
}
